import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { buildClientSlugFields } from '../../shared/clients/companyClientRouting';
import { createClient, upsertClient } from '../../api/companyRecords';
import './ClientDialog.css';

const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return email === '' || emailRegex.test(email);
};

const isValidPhone = (phone) => {
  if (phone === '') return true;
  const cleanPhone = phone.replace(/\D/g, '');
  return cleanPhone.length === 10;
};

const formatPhoneNumber = (phone) => {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 0) return '';
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
};

const ClientDialog = ({ open, onClose, client, existingClients = [], onSave }) => {
  const { user, companyData } = useAuth();
  const [clientData, setClientData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: {
      street1: '',
      street2: '',
      city: '',
      state: '',
      zipCode: ''
    }
  });

  const [errors, setErrors] = useState({
    email: '',
    phone: ''
  });

  useEffect(() => {
    if (client) {
      setClientData({
        ...client,
        address: {
          street1: client.address?.street1 || '',
          street2: client.address?.street2 || '',
          city: client.address?.city || '',
          state: client.address?.state || '',
          zipCode: client.address?.zipCode || ''
        }
      });
    } else {
      setClientData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: {
          street1: '',
          street2: '',
          city: '',
          state: '',
          zipCode: ''
        }
      });
    }
  }, [client]);

  useEffect(() => {
    if (!open) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Special handling for phone so formatting and validation stay in sync
    if (name === 'phone') {
      const formatted = formatPhoneNumber(value);
      setClientData((prev) => ({ ...prev, phone: formatted }));

      if (value.trim() === '') {
        setErrors((prev) => ({
          ...prev,
          phone: 'Phone is required'
        }));
        return;
      }

      setErrors((prev) => ({
        ...prev,
        phone: !isValidPhone(formatted) ? 'Please enter a valid 10-digit phone number' : ''
      }));

      return;
    }

    setClientData((prev) => ({ ...prev, [name]: value }));

    if (value.trim() === '') {
      if (name === 'firstName' || name === 'lastName' || name === 'email') {
        setErrors((prev) => ({
          ...prev,
          [name]: `${name.charAt(0).toUpperCase() + name.slice(1)} is required`
        }));
      }
      return;
    }

    if (name === 'email') {
      setErrors((prev) => ({
        ...prev,
        email: !isValidEmail(value) ? 'Please enter a valid email address' : ''
      }));
    } else {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const updateAddress = (field, value) => {
    setClientData((prev) => ({
      ...prev,
      address: { ...prev.address, [field]: value }
    }));
  };

  const hasRequiredFields = () => {
    if (
      !clientData.firstName?.trim() ||
      !clientData.lastName?.trim() ||
      !clientData.email?.trim() ||
      !clientData.phone?.trim()
    ) {
      return false;
    }
    if (!!errors.email || !!errors.phone) return false;
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasRequiredFields()) {
      setErrors((prev) => ({
        ...prev,
        email: !clientData.email?.trim()
          ? 'Email is required'
          : !isValidEmail(clientData.email)
          ? 'Please enter a valid email address'
          : '',
        phone: !clientData.phone?.trim()
          ? 'Phone is required'
          : !isValidPhone(clientData.phone)
          ? 'Please enter a valid 10-digit phone number'
          : ''
      }));
      return;
    }

    try {
      if (!user || !companyData) throw new Error('No authenticated user or company data');

      const fullName = `${clientData.firstName.trim()} ${clientData.lastName.trim()}`;
      const { category: _category, ...clientFields } = clientData;
      const slugFields = buildClientSlugFields(
        { ...clientFields, name: fullName },
        existingClients,
        client?.id || null
      );
      const now = new Date().toISOString();
      const clientDataToSave = {
        ...clientFields,
        ...slugFields,
        name: fullName,
        searchName: fullName.toLowerCase(),
        archived: false,
        createdAt: client ? client.createdAt : now,
        updatedAt: now,
        avatarUrl: client?.avatarUrl || ''
      };

      if (client) {
        await upsertClient({
          companyId: companyData.id,
          id: client.id,
          client: clientDataToSave,
        });
      } else {
        await createClient({
          companyId: companyData.id,
          client: clientDataToSave,
        });
      }
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving client: ', error);
    }
  };

  if (!open) return null;

  return (
    <div
      className={`client-dialog-backdrop ${open ? 'client-dialog-backdrop--open' : ''}`}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="client-dialog-title"
    >
      <div className="client-dialog-panel" onClick={(e) => e.stopPropagation()}>
        <header className="client-dialog-header">
          <h2 id="client-dialog-title" className="client-dialog-title">
            {client ? 'Edit Client' : 'Add New Client'}
          </h2>
          <p className="client-dialog-required-hint">* Required fields</p>
        </header>

        <form onSubmit={handleSubmit} className="client-dialog-body">
          <section className="client-dialog-section">
            <h3 className="client-dialog-section-title">Basic Information</h3>

            <div className="client-dialog-field">
              <label htmlFor="client-firstName" className="client-dialog-label client-dialog-label--required">
                First Name
              </label>
              <input
                id="client-firstName"
                type="text"
                name="firstName"
                className="client-dialog-input"
                value={clientData.firstName}
                onChange={handleInputChange}
                placeholder="Enter client's first name"
                autoComplete="given-name"
              />
            </div>

            <div className="client-dialog-field">
              <label htmlFor="client-lastName" className="client-dialog-label client-dialog-label--required">
                Last Name
              </label>
              <input
                id="client-lastName"
                type="text"
                name="lastName"
                className="client-dialog-input"
                value={clientData.lastName}
                onChange={handleInputChange}
                placeholder="Enter client's last name"
                autoComplete="family-name"
              />
            </div>

            <div className="client-dialog-field">
              <label htmlFor="client-email" className="client-dialog-label client-dialog-label--required">
                Email
              </label>
              <input
                id="client-email"
                type="email"
                name="email"
                className={`client-dialog-input ${errors.email ? 'client-dialog-input--error' : ''}`}
                value={clientData.email}
                onChange={handleInputChange}
                placeholder="Client's email address"
                autoComplete="email"
              />
              <span className={`client-dialog-helper ${errors.email ? 'client-dialog-helper--error' : ''}`}>
                {errors.email || "Client's email address for communications"}
              </span>
            </div>

            <div className="client-dialog-field">
              <label htmlFor="client-phone" className="client-dialog-label client-dialog-label--required">
                Phone
              </label>
              <input
                id="client-phone"
                type="tel"
                name="phone"
                className={`client-dialog-input ${errors.phone ? 'client-dialog-input--error' : ''}`}
                value={clientData.phone}
                onChange={handleInputChange}
                placeholder="(555) 123-4567"
                autoComplete="tel"
              />
              <span className={`client-dialog-helper ${errors.phone ? 'client-dialog-helper--error' : ''}`}>
                {errors.phone || "Client's phone number (e.g., (555) 123-4567)"}
              </span>
            </div>
          </section>

          <section className="client-dialog-section">
            <h3 className="client-dialog-section-title">Address</h3>

            <div className="client-dialog-field">
              <label htmlFor="client-street1" className="client-dialog-label">
                Street Address
              </label>
              <input
                id="client-street1"
                type="text"
                className="client-dialog-input"
                value={clientData.address.street1}
                onChange={(e) => updateAddress('street1', e.target.value)}
                placeholder="Street address"
                autoComplete="street-address"
              />
            </div>

            <div className="client-dialog-field">
              <label htmlFor="client-street2" className="client-dialog-label">
                Apt, Suite, etc. (optional)
              </label>
              <input
                id="client-street2"
                type="text"
                className="client-dialog-input"
                value={clientData.address.street2}
                onChange={(e) => updateAddress('street2', e.target.value)}
                placeholder="Apt, suite, unit"
                autoComplete="address-line2"
              />
            </div>

            <div className="client-dialog-row">
              <div className="client-dialog-field">
                <label htmlFor="client-city" className="client-dialog-label">
                  City
                </label>
                <input
                  id="client-city"
                  type="text"
                  className="client-dialog-input"
                  value={clientData.address.city}
                  onChange={(e) => updateAddress('city', e.target.value)}
                  placeholder="City"
                  autoComplete="address-level2"
                />
              </div>
              <div className="client-dialog-field">
                <label htmlFor="client-state" className="client-dialog-label">
                  State
                </label>
                <input
                  id="client-state"
                  type="text"
                  className="client-dialog-input"
                  value={clientData.address.state}
                  onChange={(e) => updateAddress('state', e.target.value)}
                  placeholder="State"
                  autoComplete="address-level1"
                />
              </div>
              <div className="client-dialog-field">
                <label htmlFor="client-zipCode" className="client-dialog-label">
                  ZIP Code
                </label>
                <input
                  id="client-zipCode"
                  type="text"
                  className="client-dialog-input"
                  value={clientData.address.zipCode}
                  onChange={(e) => updateAddress('zipCode', e.target.value)}
                  placeholder="ZIP"
                  autoComplete="postal-code"
                />
              </div>
            </div>
          </section>
        </form>

        <footer className="client-dialog-footer">
          <button
            type="button"
            className="client-dialog-btn client-dialog-btn--danger"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="client-dialog-btn client-dialog-btn--primary"
            disabled={!hasRequiredFields()}
            onClick={handleSubmit}
          >
            {client ? 'Save Changes' : 'Add Client'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default ClientDialog;
